/**
 * A3 — Registrar (o editar) una comunicación manual: llamada, reunión, WhatsApp, carta,
 * visita… Con cliente queda asignada a su ficha; sin cliente va a «Sin asignar».
 * WhatsApp: la app NO envía nada; abre WhatsApp con el mensaje escrito y lo envía la persona.
 */
import { useEffect, useMemo, useState, type FormEvent } from 'react'
import { ExternalLink, Loader2 } from 'lucide-react'
import { api, ApiError } from '../../lib/api'
import Modal from '../ui/Modal'
import { inputCls, useChannels, whatsappLink } from './common'
import {
  CommunicationChannelLabel,
  CommunicationDirectionLabel,
  type CaseListResponse,
  type CommunicationChannelKind,
  type CommunicationDirection,
  type CommunicationDto,
  type DocumentListItem,
} from '@toga/shared'

interface Props {
  clientId: string
  /** Teléfono del cliente, para el enlace de WhatsApp y para rellenar la contraparte. */
  clientPhone?: string | null
  /** Si viene, se edita esa comunicación. */
  initial?: CommunicationDto | null
  onClose: () => void
  onSaved: () => void
}

const pad = (n: number) => String(n).padStart(2, '0')
/** Fecha local para <input type="datetime-local">. */
function toLocalInput(iso?: string): string {
  const d = iso ? new Date(iso) : new Date()
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
}

const DEFAULT_DIRECTION: Record<CommunicationChannelKind, CommunicationDirection> = {
  EMAIL: 'INBOUND',
  PHONE: 'INBOUND',
  MEETING: 'BOTH',
  WHATSAPP: 'INBOUND',
  LETTER: 'INBOUND',
  VISIT: 'BOTH',
  OTHER: 'BOTH',
}

export default function RegisterCommunicationModal({
  clientId,
  clientPhone,
  initial,
  onClose,
  onSaved,
}: Props) {
  const channels = useChannels()
  const [cases, setCases] = useState<CaseListResponse['items']>([])
  const [docs, setDocs] = useState<DocumentListItem[]>([])

  const [channel, setChannel] = useState<CommunicationChannelKind>(initial?.channel ?? 'PHONE')
  const [direction, setDirection] = useState<CommunicationDirection>(
    initial?.direction ?? DEFAULT_DIRECTION['PHONE']
  )
  const [occurredAt, setOccurredAt] = useState(toLocalInput(initial?.occurredAt))
  const [subject, setSubject] = useState(initial?.subject ?? '')
  const [summary, setSummary] = useState(initial?.summary ?? '')
  const [caseId, setCaseId] = useState(initial?.caseId ?? '')
  const [name, setName] = useState(initial?.counterpartName ?? '')
  const [email, setEmail] = useState(initial?.counterpartEmail ?? '')
  const [phone, setPhone] = useState(initial?.counterpartPhone ?? '')
  const [picked, setPicked] = useState<Set<string>>(
    new Set(initial?.documents.map((d) => d.id) ?? [])
  )
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    api
      .get<CaseListResponse>(`/cases?clientId=${clientId}&pageSize=100`)
      .then((r) => setCases(r.items))
      .catch(() => setCases([]))
    api
      .get<{ items: DocumentListItem[] }>(`/documents?clientId=${clientId}&pageSize=100`)
      .then((r) => setDocs(r.items))
      .catch(() => setDocs([]))
  }, [clientId])

  // Documentos adjuntables: del cliente sin expediente o del expediente elegido.
  const attachable = useMemo(
    () => docs.filter((d) => !d.caseId || d.caseId === (caseId || null)),
    [docs, caseId]
  )

  const allowed = useMemo(
    () =>
      (channels?.items ?? [])
        .filter((c) => c.enabled || c.kind === initial?.channel)
        .map((c) => c.kind),
    [channels, initial]
  )

  const pickChannel = (k: CommunicationChannelKind) => {
    setChannel(k)
    if (!initial) setDirection(DEFAULT_DIRECTION[k])
  }

  const wa = channel === 'WHATSAPP' ? whatsappLink(phone || clientPhone, summary || subject) : null

  const submit = async (e: FormEvent) => {
    e.preventDefault()
    setError(null)
    setBusy(true)
    try {
      const body = {
        channel,
        direction,
        occurredAt: new Date(occurredAt).toISOString(),
        subject: subject.trim() || null,
        summary: summary.trim(),
        counterpartName: name.trim() || null,
        counterpartEmail: email.trim() || null,
        counterpartPhone: phone.trim() || null,
      }
      let id = initial?.id
      if (initial) {
        await api.patch(`/communications/${initial.id}`, { ...body, caseId: caseId || null })
      } else {
        const r = await api.post<{ communication: { id: string } }>('/communications', {
          ...body,
          clientId,
          caseId: caseId || null,
        })
        id = r.communication.id
      }
      if (id) {
        const before = new Set(initial?.documents.map((d) => d.id) ?? [])
        for (const docId of picked) {
          if (!before.has(docId))
            await api.post(`/communications/${id}/documents`, { documentId: docId })
        }
        for (const docId of before) {
          if (!picked.has(docId)) await api.delete(`/communications/${id}/documents/${docId}`)
        }
      }
      onSaved()
    } catch (err) {
      if (err instanceof ApiError) {
        const b = err.body as
          | { error?: string; issues?: { fieldErrors?: Record<string, string[]> } }
          | undefined
        const first = b?.issues?.fieldErrors
          ? Object.values(b.issues.fieldErrors).flat().find(Boolean)
          : undefined
        setError(first ?? b?.error ?? 'No se pudo guardar')
      } else {
        setError('Error de red')
      }
    } finally {
      setBusy(false)
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={initial ? 'Editar comunicación' : 'Registrar comunicación'}
      description="Deja constancia de una llamada, reunión, WhatsApp, carta o visita con este cliente."
      maxWidth="max-w-2xl"
    >
      <form onSubmit={submit} className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <label className="text-xs t-muted">
            Canal
            <select
              value={channel}
              onChange={(e) => pickChannel(e.target.value as CommunicationChannelKind)}
              className={`${inputCls} mt-1`}
            >
              {(allowed.length
                ? allowed
                : (Object.keys(CommunicationChannelLabel) as CommunicationChannelKind[])
              ).map((k) => (
                <option key={k} value={k}>
                  {CommunicationChannelLabel[k]}
                </option>
              ))}
            </select>
          </label>
          <label className="text-xs t-muted">
            Fecha y hora
            <input
              type="datetime-local"
              value={occurredAt}
              onChange={(e) => setOccurredAt(e.target.value)}
              className={`${inputCls} mt-1`}
              required
            />
          </label>
        </div>

        <fieldset className="flex flex-wrap gap-2">
          <legend className="text-xs t-muted mb-1">Sentido</legend>
          {(Object.keys(CommunicationDirectionLabel) as CommunicationDirection[]).map((d) => (
            <label
              key={d}
              className={[
                'px-3 py-1.5 rounded-lg border text-xs cursor-pointer',
                direction === d
                  ? 'border-toga-500/50 bg-toga-500/10 t-primary'
                  : 'border-base t-secondary',
              ].join(' ')}
            >
              <input
                type="radio"
                name="direction"
                className="sr-only"
                checked={direction === d}
                onChange={() => setDirection(d)}
              />
              {CommunicationDirectionLabel[d]}
            </label>
          ))}
        </fieldset>

        <label className="text-xs t-muted block">
          Asunto (opcional)
          <input
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            maxLength={300}
            className={`${inputCls} mt-1`}
            placeholder="Ej.: Documentación de la herencia"
          />
        </label>
        <label className="text-xs t-muted block">
          Resumen
          <textarea
            value={summary}
            onChange={(e) => setSummary(e.target.value)}
            rows={4}
            maxLength={5000}
            className={`${inputCls} mt-1`}
            placeholder="Qué se habló o qué se acordó"
          />
        </label>

        {wa && (
          <a
            href={wa}
            target="_blank"
            rel="noreferrer noopener"
            className="inline-flex items-center gap-1.5 text-xs text-emerald-300 hover:underline"
          >
            <ExternalLink size={12} /> Abrir WhatsApp con este mensaje (lo envías tú)
          </a>
        )}

        <div className="grid grid-cols-3 gap-3">
          <label className="text-xs t-muted">
            Con quién (nombre)
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              className={`${inputCls} mt-1`}
            />
          </label>
          <label className="text-xs t-muted">
            Teléfono
            <input
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder={clientPhone ?? ''}
              className={`${inputCls} mt-1`}
            />
          </label>
          <label className="text-xs t-muted">
            Email
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className={`${inputCls} mt-1`}
            />
          </label>
        </div>

        {cases.length > 0 && (
          <label className="text-xs t-muted block">
            Expediente (opcional)
            <select
              value={caseId}
              onChange={(e) => setCaseId(e.target.value)}
              className={`${inputCls} mt-1`}
            >
              <option value="">Sin expediente: de todo el cliente</option>
              {cases.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.code} · {c.title}
                </option>
              ))}
            </select>
          </label>
        )}

        {attachable.length > 0 && (
          <fieldset>
            <legend className="text-xs t-muted mb-1">Adjuntar documentos ya subidos</legend>
            <div className="max-h-32 overflow-y-auto rounded-lg border border-base p-2 space-y-1">
              {attachable.map((d) => (
                <label
                  key={d.id}
                  className="flex items-center gap-2 text-xs t-secondary cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={picked.has(d.id)}
                    onChange={(e) =>
                      setPicked((p) => {
                        const n = new Set(p)
                        if (e.target.checked) n.add(d.id)
                        else n.delete(d.id)
                        return n
                      })
                    }
                  />
                  {d.fileName}
                </label>
              ))}
            </div>
          </fieldset>
        )}

        {error && (
          <div className="text-xs rounded-lg px-3 py-2 border text-rose-300 bg-rose-500/10 border-rose-500/30">
            {error}
          </div>
        )}

        <div className="flex justify-end gap-2 pt-1">
          <button
            type="button"
            onClick={onClose}
            className="px-3.5 py-2 rounded-lg text-sm border border-base t-secondary hover:bg-hover"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={busy}
            className="px-4 py-2 rounded-lg text-sm font-semibold text-white bg-gradient-to-r from-toga-600 via-toga-500 to-toga-700 hover:shadow-glow-toga disabled:opacity-50 flex items-center gap-2"
          >
            {busy && <Loader2 size={14} className="animate-spin" />}
            {initial ? 'Guardar' : 'Registrar'}
          </button>
        </div>
      </form>
    </Modal>
  )
}
